import { useState } from 'react';
import { cn } from '@/lib/utils';
import type { CashFlowCategory } from '@progear/finance-core';
import { COST_CATS, INCOME_CATS, LINE_META } from '@/components/cash-flow/categoryMeta';
import type { CashFlowRequestOptions } from '@/lib/cashFlow';
import { ProjectSelect } from './ProjectSelect';
import {
  hasActiveOverride,
  isOverrideDirty,
  overrideDefaultsFrom,
  overrideDraftFrom,
  overridePatchFrom,
  type CashFlowEntryDetail,
  type OverrideDraft,
  type OverridePatch,
} from '@/lib/cashFlowDetail';

/**
 * The editable enrichment of an entry (module 1.3): Liik (reclassification),
 * Projekt (kood + nimi) and Kommentaar. A Liik/Projekt change is a per-entry
 * OVERRIDE that WINS over the §1.11 chart-of-accounts mapping default; clearing a
 * field back to the default reverts the override. The classification change is
 * audited (money_audit_log) even though no amount moves. The dirty-state + patch
 * mapping live in `cashFlowDetail` (lib, unit-tested) — this component only wires
 * the form to those helpers and the mutations.
 */

/** `null` → empty input; an empty input → `null` (we never persist `""`). */
function orEmpty(value: string | null): string {
  return value ?? '';
}
function emptyToNull(value: string): string | null {
  return value === '' ? null : value;
}

export interface EnrichmentFormProps {
  detail: CashFlowEntryDetail;
  isSaving: boolean;
  onSaveOverride: (patch: OverridePatch) => void;
  onSaveComment: (comment: string | null) => void;
  /** Threaded to the searchable project picker's shared query (base URL / auth / fetch). */
  requestOptions?: CashFlowRequestOptions;
}

export function EnrichmentForm({ detail, isSaving, onSaveOverride, onSaveComment, requestOptions }: EnrichmentFormProps) {
  const { entry, classification } = detail;
  const liikOptions = entry.kind === 'income' ? INCOME_CATS : COST_CATS;

  const [draft, setDraft] = useState<OverrideDraft>(() => overrideDraftFrom(classification));
  const [comment, setComment] = useState<string>(orEmpty(entry.comment));
  const [liikOpen, setLiikOpen] = useState(false);

  const overrideDirty = isOverrideDirty(draft, classification);
  const commentDirty = comment !== orEmpty(entry.comment);
  const dirty = overrideDirty || commentDirty;
  const showRevert = hasActiveOverride(classification);

  const selectLiik = (category: CashFlowCategory) => {
    setDraft((d) => ({ ...d, category }));
    setLiikOpen(false);
  };

  const revert = () => setDraft(overrideDefaultsFrom(classification));

  const save = () => {
    if (overrideDirty) onSaveOverride(overridePatchFrom(draft, classification));
    if (commentDirty) onSaveComment(emptyToNull(comment));
  };

  return (
    <section className="rounded-lg border border-border bg-card/40">
      <header className="flex items-center gap-2 border-b border-border/70 px-3 py-2">
        <h3 className="text-[12px] font-semibold uppercase tracking-wide text-foreground">Rikastamine</h3>
        <span className="ml-auto text-[10px] text-muted-foreground/70">muudatus → audit-logi</span>
      </header>

      <div className="flex flex-col gap-3 px-3 py-3">
        {/* Liik — reclassification */}
        <div className="flex flex-col gap-1">
          <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground/70">
            <span aria-hidden>#</span> Liik · ümberklassifitseerimine
          </span>
          <div className="relative">
            <button
              type="button"
              onClick={() => setLiikOpen((o) => !o)}
              className="flex w-full items-center justify-between rounded border border-border bg-background px-2 py-1.5 text-[12px] text-foreground hover:border-[hsl(var(--primary))]/50"
            >
              <span>{LINE_META[draft.category].label}</span>
              <span aria-hidden className="text-muted-foreground">▾</span>
            </button>
            {liikOpen && (
              <div className="absolute z-20 mt-1 w-full overflow-hidden rounded border border-border bg-popover shadow-lg">
                {liikOptions.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => selectLiik(opt)}
                    className={cn(
                      'flex w-full items-center justify-between px-2 py-1.5 text-left text-[12px] hover:bg-accent',
                      opt === draft.category && 'text-[hsl(var(--primary))]',
                    )}
                  >
                    {LINE_META[opt].label}
                    {opt === classification.category.default && (
                      <span className="text-[9px] text-muted-foreground/70">kontoplaan</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1.5 text-[10px]">
            <span className="text-muted-foreground/60">
              vaikimisi (§1.11 kontoplaan):{' '}
              <span className="text-foreground/70">{LINE_META[classification.category.default].label}</span>
            </span>
            {overrideDirty && (
              <span className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/10 px-1.5 py-0.5 font-medium text-[hsl(var(--primary))]">
                muudetud → ülekirjutus võidab
              </span>
            )}
          </div>
        </div>

        {/* Projekt — pick/search an existing project (no free text) */}
        <div className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground/70">Projekt</span>
          <ProjectSelect
            value={{ project: draft.project, projectName: draft.projectName }}
            onChange={(next) => setDraft((d) => ({ ...d, project: next.project, projectName: next.projectName }))}
            requestOptions={requestOptions}
          />
        </div>

        {/* Kommentaar */}
        <div className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground/70">Kommentaar</span>
          <textarea
            aria-label="Kommentaar"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            className="resize-none rounded border border-border bg-background px-2 py-1.5 text-[12px] text-foreground outline-none focus:border-[hsl(var(--primary))]/50"
          />
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={!dirty || isSaving}
            onClick={save}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12px] font-medium transition-colors',
              dirty && !isSaving
                ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]'
                : 'cursor-not-allowed bg-muted/40 text-muted-foreground/50',
            )}
          >
            <span aria-hidden>⤓</span> {isSaving ? 'Salvestab…' : 'Salvesta'}
          </button>
          {showRevert && (
            <button
              type="button"
              onClick={revert}
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-[11px] text-muted-foreground hover:bg-accent"
            >
              <span aria-hidden>↺</span> Taasta vaikimisi
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
